/**
 * Electrical Installation Types for MEV-Med Frontend
 * Instalações elétricas (Prontuário NR-10) — contrato da issue #364.
 *
 * O servidor calcula o resumo (`summary`); a tela só mostra. Datas viajam como texto `YYYY-MM-DD`.
 * Os arquivos nunca trazem o caminho de gravação.
 */

export const ELECTRICAL_INSTALLATION_TYPES = [
  'SUBESTACAO',
  'QUADRO_DE_DISTRIBUICAO',
  'CCM',
  'SPDA_ATERRAMENTO',
  'GRUPO_GERADOR',
  'INSTALACAO_PREDIAL',
  'OUTRA',
] as const;
export type ElectricalInstallationType = (typeof ELECTRICAL_INSTALLATION_TYPES)[number];

export const ELECTRICAL_INSTALLATION_STATUSES = ['EM_OPERACAO', 'EM_MANUTENCAO', 'DESATIVADA'] as const;
export type ElectricalInstallationStatus = (typeof ELECTRICAL_INSTALLATION_STATUSES)[number];

export const ELECTRICAL_RECORD_TYPES = ['INSPECAO', 'MANUTENCAO'] as const;
export type ElectricalRecordType = (typeof ELECTRICAL_RECORD_TYPES)[number];

export const ELECTRICAL_RECORD_RESULTS = ['CONFORME', 'COM_RESSALVAS', 'NAO_CONFORME'] as const;
export type ElectricalRecordResult = (typeof ELECTRICAL_RECORD_RESULTS)[number];

export const ELECTRICAL_DOCUMENT_KINDS = ['DIAGRAMA_UNIFILAR', 'LAUDO', 'DOCUMENTACAO_TECNICA', 'OUTRO'] as const;
export type ElectricalDocumentKind = (typeof ELECTRICAL_DOCUMENT_KINDS)[number];

export type ElectricalVoltageClass = 'NAO_SE_APLICA' | 'BAIXA_TENSAO' | 'ALTA_TENSAO';

/** Situação calculada pelo servidor; nunca é gravada. */
export type ElectricalInstallationSituation = 'EM_CADASTRO' | 'EM_DIA' | 'ATENCAO' | 'DESATIVADA';

export const ROTULO_DO_TIPO_DE_INSTALACAO: Readonly<Record<ElectricalInstallationType, string>> = {
  SUBESTACAO: 'Subestação',
  QUADRO_DE_DISTRIBUICAO: 'Quadro de distribuição',
  CCM: 'Centro de controle de motores (CCM)',
  SPDA_ATERRAMENTO: 'SPDA e aterramento',
  GRUPO_GERADOR: 'Grupo gerador',
  INSTALACAO_PREDIAL: 'Instalação predial',
  OUTRA: 'Outra',
};

export const ROTULO_DO_STATUS_DA_INSTALACAO: Readonly<Record<ElectricalInstallationStatus, string>> = {
  EM_OPERACAO: 'Em operação',
  EM_MANUTENCAO: 'Em manutenção',
  DESATIVADA: 'Desativada',
};

export const ROTULO_DO_TIPO_DE_REGISTRO: Readonly<Record<ElectricalRecordType, string>> = {
  INSPECAO: 'Inspeção',
  MANUTENCAO: 'Manutenção',
};

export const ROTULO_DO_RESULTADO: Readonly<Record<ElectricalRecordResult, string>> = {
  CONFORME: 'Conforme',
  COM_RESSALVAS: 'Com ressalvas',
  NAO_CONFORME: 'Não conforme',
};

export const ROTULO_DO_TIPO_DE_DOCUMENTO: Readonly<Record<ElectricalDocumentKind, string>> = {
  DIAGRAMA_UNIFILAR: 'Diagrama unifilar',
  LAUDO: 'Laudo',
  DOCUMENTACAO_TECNICA: 'Documentação técnica',
  OUTRO: 'Outro',
};

export const ROTULO_DA_CLASSE_DE_TENSAO: Readonly<Record<ElectricalVoltageClass, string>> = {
  NAO_SE_APLICA: 'Não se aplica',
  BAIXA_TENSAO: 'Baixa tensão',
  ALTA_TENSAO: 'Alta tensão',
};

export const ROTULO_DA_SITUACAO_DA_INSTALACAO: Readonly<Record<ElectricalInstallationSituation, string>> = {
  EM_CADASTRO: 'Em cadastro',
  EM_DIA: 'Em dia',
  ATENCAO: 'Atenção',
  DESATIVADA: 'Desativada',
};

export interface ElectricalRecord {
  id: number;
  installationId: number;
  recordType: ElectricalRecordType;
  performedAt: string;
  result: ElectricalRecordResult;
  nextDueDate: string | null;
  responsible: string | null;
  observations: string | null;
}

export interface ElectricalInstallationFile {
  id: number;
  installationId: number;
  documentKind: ElectricalDocumentKind;
  title: string;
  /** Datas em texto `YYYY-MM-DD`. */
  issuedAt: string | null;
  validUntil: string | null;
  originalFileName: string;
  fileMimeType: string;
  fileSize: number;
  createdAt?: string;
}

/** Resumo calculado pelo servidor; a tela só mostra. */
export interface ElectricalInstallationSummary {
  voltageClass: ElectricalVoltageClass;
  lastInspection: ElectricalRecord | null;
  nextDueDate: string | null;
  overdue: boolean;
  documentCount: number;
  situation: ElectricalInstallationSituation;
}

export interface ElectricalInstallation {
  id: number;
  companyId: number;
  departmentId: number | null;
  name: string;
  tag: string | null;
  installationType: ElectricalInstallationType;
  /** Obrigatória quando o tipo é `OUTRA`. */
  typeDescription: string | null;
  status: ElectricalInstallationStatus;
  location: string | null;
  /** Tensão nominal em volts. */
  nominalVoltage: number | null;
  observations: string | null;
  summary: ElectricalInstallationSummary;
  records?: ElectricalRecord[];
  files?: ElectricalInstallationFile[];
}

export interface ElectricalInstallationFilters {
  search?: string;
  departmentId?: number;
  situation?: ElectricalInstallationSituation;
}

export interface ElectricalInstallationListResponse {
  installations: ElectricalInstallation[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type ElectricalInstallationFormData = Pick<ElectricalInstallation, 'name' | 'installationType'> &
  Partial<
    Omit<
      ElectricalInstallation,
      'id' | 'companyId' | 'name' | 'installationType' | 'summary' | 'records' | 'files'
    >
  >;

/** Criação: quem não tem empresa ativa (plataforma, clínica) diz a empresa no corpo. */
export type ElectricalInstallationCreateData = ElectricalInstallationFormData & { companyId?: number };

export type ElectricalRecordFormData = Omit<ElectricalRecord, 'id' | 'installationId' | 'responsible'> & {
  responsible: string;
};

/** Dados que acompanham o arquivo no envio (multipart). */
export interface ElectricalInstallationFileUploadData {
  documentKind: ElectricalDocumentKind;
  title: string;
  issuedAt?: string | null;
  validUntil?: string | null;
}
